import { useSdk } from '@fondationcss/sdk'
import type {
  PagedResult,
  BoardSummaryResponse,
  BoardDetailResponse,
  CategoryResponse,
  StatusResponse,
  PostSummaryResponse,
  PostDetailResponse,
  CommentResponse,
  VoteResponse,
  NotificationResponse,
  SubscriptionStatusResponse,
  DashboardResponse,
  RoadmapColumnResponse,
  GithubIssueResponse,
  BoardGithubRepositoryResponse,
} from './model'
import type {
  CreateBoardRequest,
  UpdateBoardRequest,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  CreateStatusRequest,
  UpdateStatusRequest,
  CreatePostRequest,
  UpdatePostRequest,
  PostQueryParams,
  ChangeStatusRequest,
  MergePostRequest,
  CreateCommentRequest,
  UpdateCommentRequest,
  LinkGithubIssueRequest,
  CreateGithubIssueRequest,
  AddBoardGithubRepositoryRequest,
} from './dto'

function toQueryString(params: object): string {
  const entries = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
  return entries.length ? `?${entries.join('&')}` : ''
}

export const getCustomSdk = () => {
  const sdk = useSdk()
  const api = sdk.fetchWrapper

  /** Resolve a relative API path (e.g. /boards/{id}/logo) to a full URL */
  const resolveApiUrl = (path: string) => {
    const base = sdk.settings.value?.apiUrl ?? ''
    return `${base}${path}`
  }

  return {
    resolveApiUrl,
    // ── Boards ────────────────────────────────────────────────────
    getBoards: () =>
      api.get<BoardSummaryResponse[]>('/boards'),
    getBoard: (slug: string) =>
      api.get<BoardDetailResponse>(`/boards/slug/${encodeURIComponent(slug)}`),
    createBoard: (dto: CreateBoardRequest) =>
      api.post<BoardSummaryResponse>('/boards', dto),
    updateBoard: (id: string, dto: UpdateBoardRequest) =>
      api.put<BoardSummaryResponse>(`/boards/${id}`, dto),
    archiveBoard: (id: string) =>
      api.put<void>(`/boards/${id}/archive`, {}),
    uploadBoardLogo: async (id: string, file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const baseUrl = sdk.settings.value?.apiUrl ?? ''
      const res = await fetch(`${baseUrl}/boards/${id}/logo`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
      return res.json() as Promise<BoardDetailResponse>
    },
    deleteBoardLogo: (id: string) =>
      api.delete<BoardDetailResponse>(`/boards/${id}/logo`),

    // ── Categories ────────────────────────────────────────────────
    createCategory: (boardId: string, dto: CreateCategoryRequest) =>
      api.post<CategoryResponse>(`/boards/${boardId}/categories`, dto),
    updateCategory: (id: string, dto: UpdateCategoryRequest) =>
      api.put<CategoryResponse>(`/categories/${id}`, dto),
    archiveCategory: (id: string) =>
      api.put<void>(`/categories/${id}/archive`, {}),

    // ── Statuses ──────────────────────────────────────────────────
    createStatus: (boardId: string, dto: CreateStatusRequest) =>
      api.post<StatusResponse>(`/boards/${boardId}/statuses`, dto),
    updateStatus: (id: string, dto: UpdateStatusRequest) =>
      api.put<StatusResponse>(`/statuses/${id}`, dto),
    archiveStatus: (id: string) =>
      api.put<void>(`/statuses/${id}/archive`, {}),

    // ── Posts ─────────────────────────────────────────────────────
    getPosts: (boardId: string, params: PostQueryParams = {}) =>
      api.get<PagedResult<PostSummaryResponse>>(`/boards/${boardId}/posts${toQueryString(params)}`),
    getPost: (id: string) =>
      api.get<PostDetailResponse>(`/posts/${id}`),
    createPost: (boardId: string, dto: CreatePostRequest) =>
      api.post<PostDetailResponse>(`/boards/${boardId}/posts`, dto),
    updatePost: (id: string, dto: UpdatePostRequest) =>
      api.put<PostDetailResponse>(`/posts/${id}`, dto),
    archivePost: (id: string) =>
      api.put<void>(`/posts/${id}/archive`, {}),
    changePostStatus: (id: string, dto: ChangeStatusRequest) =>
      api.put<void>(`/posts/${id}/status`, dto),
    mergePost: (id: string, dto: MergePostRequest) =>
      api.put<void>(`/posts/${id}/merge`, dto),
    findSimilarPosts: (boardId: string, title: string) =>
      api.get<PostSummaryResponse[]>(`/boards/${boardId}/posts/similar${toQueryString({ title })}`),

    // ── Votes ─────────────────────────────────────────────────────
    toggleVote: (postId: string) =>
      api.post<VoteResponse>(`/posts/${postId}/vote`, {}),

    // ── Comments ──────────────────────────────────────────────────
    createComment: (postId: string, dto: CreateCommentRequest) =>
      api.post<CommentResponse>(`/posts/${postId}/comments`, dto),
    updateComment: (id: string, dto: UpdateCommentRequest) =>
      api.put<CommentResponse>(`/comments/${id}`, dto),
    archiveComment: (id: string) =>
      api.put<void>(`/comments/${id}/archive`, {}),

    // ── Notifications ─────────────────────────────────────────────
    getNotifications: (unreadOnly = false) =>
      api.get<NotificationResponse[]>(`/notifications${toQueryString({ unreadOnly })}`),
    markNotificationRead: (id: string) =>
      api.put<void>(`/notifications/${id}/read`, {}),
    markAllNotificationsRead: () =>
      api.put<void>('/notifications/read-all', {}),
    getUnreadCount: () =>
      api.get<{ count: number }>('/notifications/unread-count'),

    // ── Subscriptions ─────────────────────────────────────────────
    getSubscriptionStatus: (postId: string) =>
      api.get<SubscriptionStatusResponse>(`/posts/${postId}/subscription`),
    toggleSubscription: (postId: string) =>
      api.post<SubscriptionStatusResponse>(`/posts/${postId}/subscribe`, {}),

    // ── Roadmap ───────────────────────────────────────────────────
    getRoadmap: (boardId?: string) =>
      api.get<RoadmapColumnResponse[]>(boardId ? `/roadmap/${boardId}` : '/roadmap'),

    // ── Admin ─────────────────────────────────────────────────────
    getDashboard: () =>
      api.get<DashboardResponse>('/admin/dashboard'),
    hardDeletePost: (postId: string) =>
      api.delete<void>(`/admin/posts/${postId}`),

    // ── GitHub ────────────────────────────────────────────────────
    getPostGithubIssues: (postId: string) =>
      api.get<GithubIssueResponse[]>(`/posts/${postId}/github-issues`),
    linkGithubIssue: (postId: string, dto: LinkGithubIssueRequest) =>
      api.post<GithubIssueResponse>(`/posts/${postId}/github-issues`, dto),
    createGithubIssue: (postId: string, dto: CreateGithubIssueRequest) =>
      api.post<GithubIssueResponse>(`/posts/${postId}/github-issues/create`, dto),
    unlinkGithubIssue: (postId: string, linkId: string) =>
      api.put<void>(`/posts/${postId}/github-issues/${linkId}/archive`, {}),
    getBoardGithubRepositories: (boardId: string) =>
      api.get<BoardGithubRepositoryResponse[]>(`/boards/${boardId}/github-repositories`),
    addBoardGithubRepository: (boardId: string, dto: AddBoardGithubRepositoryRequest) =>
      api.post<BoardGithubRepositoryResponse>(`/boards/${boardId}/github-repositories`, dto),
    removeBoardGithubRepository: (repoId: string) =>
      api.put<void>(`/boards/github-repositories/${repoId}/archive`, {}),
  }
}
